import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
const apiUrl = import.meta.env.VITE_API_URL;
// Define VideoJob interface
interface VideoJob {
  id: string;
  project_id: string;
  status: "queued" | "processing" | "completed" | "failed";
  current_step: string | null;
  progress: number;
  error_message: string | null;
  logs: Array<{
    timestamp: string;
    message: string;
  }>;
  result_video_url: string | null;
  created_at: string;
  updated_at: string;
}

// Context menu position interface
interface ContextMenuPosition {
  x: number;
  y: number;
  jobId: string | null;
}

export default function VideoJobsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLiveUpdating, setIsLiveUpdating] = useState(false);
  const [projectName, setProjectName] = useState<string>("");

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] =
    useState<ContextMenuPosition>({ x: 0, y: 0, jobId: null });

  // Confirmation dialog state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create the fetchJobs function with useCallback to avoid recreating it on every render
  const fetchJobs = useCallback(async () => {
    if (!token) {
      setError("Please log in to view your video jobs");
      setIsLoading(false);
      return null;
    }

    // Always set loading when explicitly fetching
    setIsLoading(true);

    try {
      // Construct the URL with project_id parameter if available
      const url = projectId
        ? `${apiUrl}/api/videos/jobs?project_id=${projectId}`
        : `${apiUrl}/api/videos/jobs`;

      console.log(`Fetching job list from: ${url}`);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Update jobs state with new data
      const newJobs = response.data;
      console.log(`Received ${newJobs.length} jobs`);

      // Log processing jobs for debugging
      const processingJobs = newJobs.filter(
        (job: VideoJob) => job.status === "processing"
      );
      if (processingJobs.length > 0) {
        console.log(`Found ${processingJobs.length} processing jobs:`);
        processingJobs.forEach((job: VideoJob) => {
          console.log(`Job ${job.id}: ${job.progress}% - ${job.current_step}`);
        });
      }

      setJobs(newJobs);
      setIsLoading(false);
      setError(null);

      return newJobs;
    } catch (err: any) {
      console.error("Error fetching video jobs:", err);
      // Handle auth errors specifically
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError("Authentication failed. Please log in again.");
      } else {
        setError(err.response?.data?.detail || "Failed to load video jobs");
        toast.error("Error loading video jobs");
      }
      setIsLoading(false);
      return null;
    }
  }, [token, projectId]);

  // If projectId is available, fetch the project name
  useEffect(() => {
    const fetchProjectName = async () => {
      if (!projectId || !token) return;

      try {
        const response = await axios.get(
          `${apiUrl}/api/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.name) {
          setProjectName(response.data.name);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };

    fetchProjectName();
  }, [projectId, token]);

  // Function to handle right-click on a job card
  const handleContextMenu = (e: React.MouseEvent, jobId: string) => {
    e.preventDefault(); // Prevent default browser context menu

    // Set context menu position and job id
    setContextMenuPosition({
      x: e.clientX,
      y: e.clientY,
      jobId: jobId,
    });

    setShowContextMenu(true);
  };

  // Function to handle clicking outside the context menu to close it
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(false);
    };

    if (showContextMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showContextMenu]);

  // Function to delete a video job
  const deleteVideoJob = async (jobId: string) => {
    if (!token || !jobId) return;

    setIsDeleting(true);

    try {
      const response = await axios.delete(
        `${apiUrl}/api/videos/jobs/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // Remove the job from the state
        setJobs(jobs.filter((job) => job.id !== jobId));
        toast.success("Video job deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting video job:", error);
      toast.error("Failed to delete video job");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      setJobToDelete(null);
    }
  };

  // Start polling for updates
  const startPolling = useCallback(() => {
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log("Starting polling for job updates");

    // Start a new polling interval
    const intervalId = setInterval(async () => {
      console.log("Polling for job list updates...");
      const updatedJobs = await fetchJobs();

      // Check if we still have jobs that need monitoring
      if (updatedJobs) {
        const jobsToMonitor = updatedJobs.some(
          (job: VideoJob) =>
            job.status === "processing" || job.status === "queued"
        );

        // If no more jobs to monitor, stop polling
        if (!jobsToMonitor) {
          console.log("No more active jobs to monitor, stopping polling");
          clearInterval(intervalId);
          pollingIntervalRef.current = null;
          setIsLiveUpdating(false);
        }
      }
    }, 3000); // Poll every 3 seconds

    pollingIntervalRef.current = intervalId;
    setIsLiveUpdating(true);

    return () => {
      clearInterval(intervalId);
      pollingIntervalRef.current = null;
      setIsLiveUpdating(false);
    };
  }, [fetchJobs]);

  // Load jobs once on component mount if user and token are available
  useEffect(() => {
    if (user && token) {
      // Always fetch fresh data on mount
      console.log("Component mounted, fetching initial job data");
      fetchJobs().then((jobsData) => {
        // Start polling if there are any jobs that need monitoring
        if (
          jobsData &&
          jobsData.some(
            (job: VideoJob) =>
              job.status === "processing" || job.status === "queued"
          )
        ) {
          console.log(
            "Found jobs that need monitoring, starting automatic polling"
          );
          startPolling();
        }
      });
    } else if (!isLoading) {
      setError("Please log in to view your video jobs");
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [user, token, fetchJobs, startPolling]);

  // Convert status to badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "queued":
        return "bg-blue-100 text-blue-800";
      case "processing":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Format date for display with proper time zone handling
  const formatDate = (dateString: string) => {
    // Create date object from the ISO string (which includes timezone info)
    const date = new Date(dateString);
    
    // Calculate time difference
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    // Show relative time for recent items
    if (diffSecs < 60) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // For older timestamps, show the full date
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  // Function to manually refresh jobs and restart polling if needed
  const refreshJobsAndStartPolling = async () => {
    const jobsData = await fetchJobs();

    // If we have jobs to monitor and aren't already polling, start polling
    if (
      jobsData &&
      jobsData.some(
        (job: VideoJob) =>
          job.status === "processing" || job.status === "queued"
      ) &&
      !isLiveUpdating
    ) {
      startPolling();
      toast.success("Live updates activated", { id: "live-updates" });
    }
  };

  // Generate the page title based on whether we're viewing project-specific jobs
  const getPageTitle = () => {
    if (projectId) {
      return projectName
        ? `Video Jobs - ${projectName}`
        : `Video Jobs - Project ${projectId.slice(0, 8)}...`;
    }
    return "All Video Jobs";
  };

  return (
    <div className="container mx-auto p-5">
      <Toaster position="top-right" />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            {getPageTitle()}
            {isLiveUpdating && (
              <span className="ml-2 text-xs text-green-500 font-medium">
                (Live updates active)
              </span>
            )}
          </h1>
          {projectId && (
            <Link
              to="/video-jobs"
              className="text-sm text-blue-500 hover:underline"
            >
              ← View all video jobs
            </Link>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshJobsAndStartPolling}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh
          </button>
          {projectId && (
            <button
              onClick={() =>
                navigate(`/generate-video`, { state: { projectId } })
              }
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            >
              Generate More
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center my-8">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-3"></div>
            <p className="text-gray-600">Loading job list...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          <p>{error}</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded-md text-center">
          <p className="text-gray-600">
            {projectId
              ? "No video jobs found for this project."
              : "No video jobs found."}
          </p>
          <p className="mt-2">
            <Link
              to="/generate-video"
              className="text-blue-500 hover:underline"
            >
              Create a new video
            </Link>
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="border rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 cursor-pointer"
              onClick={() => navigate(`/video-jobs/${job.id}`)}
              onContextMenu={(e) => handleContextMenu(e, job.id)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                      job.status
                    )}`}
                  >
                    {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                  </span>
                  <h2 className="mt-2 text-lg font-semibold truncate">
                    {projectId
                      ? "Video Job"
                      : `Project: ${job.project_id.slice(0, 8)}...`}
                  </h2>
                </div>
                <div className="text-right text-sm text-gray-500">
                  {formatDate(job.created_at)}
                </div>
              </div>

              {job.status === "processing" && (
                <div className="mt-3">
                  <div className="text-sm text-gray-600 mb-1">
                    {job.current_step?.replace(/_/g, " ")}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${job.progress}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-right mt-1 text-gray-500">
                    {job.progress}%
                  </div>
                </div>
              )}

              {job.status === "queued" && (
                <div className="mt-3">
                  <div className="bg-blue-50 p-2 rounded flex items-center justify-between">
                    <span className="text-blue-700 text-sm">
                      Queued for processing
                    </span>
                    <div className="animate-pulse flex space-x-1">
                      <div className="w-1 h-1 bg-blue-700 rounded-full"></div>
                      <div className="w-1 h-1 bg-blue-700 rounded-full"></div>
                      <div className="w-1 h-1 bg-blue-700 rounded-full"></div>
                    </div>
                  </div>
                </div>
              )}

              {job.status === "completed" && job.result_video_url && (
                <div className="mt-3">
                  <div className="bg-green-50 p-2 rounded text-green-700 text-sm">
                    Video ready to view
                  </div>
                </div>
              )}

              {job.status === "failed" && (
                <div className="mt-3">
                  <div className="bg-red-50 p-2 rounded text-red-700 text-sm truncate">
                    {job.error_message || "Unknown error occurred"}
                  </div>
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300 mb-2">
                  Audio Quality Support
                </h3>
                <p className="text-blue-700 dark:text-blue-400">
                  Not happy with the audio quality? Open a support ticket and
                  we'll fix your video for free!
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent navigation to job detail
                    window.open("https://ugz.ai/support", "_blank");
                  }}
                  className="mt-3 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200"
                >
                  Contact Support
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <div
          className="fixed bg-white rounded-md shadow-lg p-1 z-50 border border-gray-200"
          style={{
            top: `${contextMenuPosition.y}px`,
            left: `${contextMenuPosition.x}px`,
          }}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
            onClick={(e) => {
              e.stopPropagation();
              setShowContextMenu(false);
              setJobToDelete(contextMenuPosition.jobId);
              setShowDeleteConfirmation(true);
            }}
          >
            Delete Job
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Delete Video Job</h3>
            <p className="mb-6">
              Are you sure you want to delete this video job? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setJobToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => jobToDelete && deleteVideoJob(jobToDelete)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
